import { useState, useEffect } from 'react';

export interface ResponsiveLayoutState {
    isCompactLayout: boolean;
    isMobileLayout: boolean;
    isTabletLayout: boolean;
    screenWidth: number;
    screenHeight: number;
    isTouchDevice: boolean;
    isDarkMode: boolean;
}

const useResponsiveLayout = (): ResponsiveLayoutState => {
    const [layoutState, setLayoutState] = useState<ResponsiveLayoutState>(() => {
        // Initialize with safe defaults
        if (typeof window !== 'undefined') {
            // Check for matchMedia function availability (older browsers/WebViews may not have it)
            const isTouchDevice = typeof window.matchMedia === 'function'
                ? window.matchMedia('(pointer: coarse)').matches
                : false;
            const isDarkMode = typeof window.matchMedia === 'function'
                ? window.matchMedia('(prefers-color-scheme: dark)').matches
                : false;

            return {
                isCompactLayout: window.innerWidth <= 1400,
                isMobileLayout: window.innerWidth <= 960,
                isTabletLayout: window.innerWidth > 768 && window.innerWidth <= 1400,
                screenWidth: window.innerWidth,
                screenHeight: window.innerHeight,
                isTouchDevice,
                isDarkMode
            };
        }
        return {
            isCompactLayout: false,
            isMobileLayout: false,
            isTabletLayout: false,
            screenWidth: 0,
            screenHeight: 0,
            isTouchDevice: false,
            isDarkMode: false
        };
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Helper function to safely check media queries on older browsers/WebViews
        const safeMatchMedia = (query: string): boolean => {
            return typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false;
        };

        // Helper function to safely create media query objects
        const safeMatchMediaObject = (query: string) => {
            return typeof window.matchMedia === 'function' ? window.matchMedia(query) : null;
        };

        const updateLayoutState = () => {
            const isCompact = window.innerWidth <= 1400;
            const isMobile = window.innerWidth <= 960;
            const isTablet = window.innerWidth > 768 && window.innerWidth <= 1400;
            const isTouch = safeMatchMedia('(pointer: coarse)');
            const isDark = safeMatchMedia('(prefers-color-scheme: dark)');

            setLayoutState(prevState => {
                // Only update if something actually changed
                if (
                    prevState.isCompactLayout !== isCompact ||
                    prevState.isMobileLayout !== isMobile ||
                    prevState.isTabletLayout !== isTablet ||
                    prevState.screenWidth !== window.innerWidth ||
                    prevState.screenHeight !== window.innerHeight ||
                    prevState.isTouchDevice !== isTouch ||
                    prevState.isDarkMode !== isDark
                ) {
                    return {
                        isCompactLayout: isCompact,
                        isMobileLayout: isMobile,
                        isTabletLayout: isTablet,
                        screenWidth: window.innerWidth,
                        screenHeight: window.innerHeight,
                        isTouchDevice: isTouch,
                        isDarkMode: isDark
                    };
                }
                return prevState;
            });
        };

        // Initial calculation
        updateLayoutState();

        // Create media query listeners (safely handle cases where matchMedia is unavailable)
        const mediaQueries = {
            compact: safeMatchMediaObject('(max-width: 1400px)'),
            mobile: safeMatchMediaObject('(max-width: 960px)'),
            tablet: safeMatchMediaObject('(min-width: 769px) and (max-width: 1400px)'),
            touch: safeMatchMediaObject('(pointer: coarse)'),
            dark: safeMatchMediaObject('(prefers-color-scheme: dark)')
        };

        const handlers = {
            compact: (e: MediaQueryListEvent) => updateLayoutState(),
            mobile: (e: MediaQueryListEvent) => updateLayoutState(),
            tablet: (e: MediaQueryListEvent) => updateLayoutState(),
            touch: (e: MediaQueryListEvent) => updateLayoutState(),
            dark: (e: MediaQueryListEvent) => updateLayoutState()
        };

        // Add event listeners with fallback for older browsers (skip if matchMedia not available)
        Object.entries(mediaQueries).forEach(([key, query]) => {
            if (!query) return; // Skip if matchMedia not available
            const handler = handlers[key as keyof typeof handlers];
            if (query.addEventListener) {
                query.addEventListener('change', handler);
            } else {
                // Fallback for older Safari and Android WebView
                (query as any).addListener(handler);
            }
        });

        // Handle resize events for viewport dimensions
        let resizeTimeout: number;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(updateLayoutState, 100);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            // Cleanup listeners with fallback for older browsers (skip if matchMedia not available)
            Object.entries(mediaQueries).forEach(([key, query]) => {
                if (!query) return; // Skip if matchMedia not available
                const handler = handlers[key as keyof typeof handlers];
                if (query.removeEventListener) {
                    query.removeEventListener('change', handler);
                } else {
                    // Fallback for older Safari and Android WebView
                    (query as any).removeListener(handler);
                }
            });
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, []);

    return layoutState;
};

export default useResponsiveLayout;
